import { createConsumer } from '@rails/actioncable';
import { emitter } from 'shared/helpers/mitt';
import logger from 'shared/helpers/logger';
import { getClientSessionId } from './clientSessionId';

// Helper to access the global i18n instance created in dashboard.js entrypoint
// (window.__APP_I18N__ — o nome antigo window.appI18n nunca existiu, então
// TODOS os toasts realtime renderizavam a chave literal; achado QA ciclo 11).
const t = (key, params) => {
  // eslint-disable-next-line no-underscore-dangle
  const i18n = window.__APP_I18N__;
  if (!i18n) return key;
  return i18n.global.t(key, params);
};

// Desconexão sustentada: avisar o usuário após este intervalo sem reconectar
// (~o tempo do backoff completo de 5 tentativas do scheduleReconnect).
const DISCONNECT_NOTIFY_AFTER_MS = 35000;

// Janela de agregação dos toasts de "card criado" (import em massa).
const CREATED_TOAST_WINDOW_MS = 2000;

// Janela de coalescência da hidratação de cards criados remotamente. Curta o
// bastante para o card completo aparecer como parte da mesma "chegada".
const HYDRATION_WINDOW_MS = 400;

// Acima deste volume numa janela (import CSV, automação em lote) sai mais
// barato — e mais correto — refazer o board do que disparar N GETs.
const HYDRATION_MAX_INDIVIDUAL = 8;

// Eventos que não espelham um update otimista local: a aba que originou a ação
// também precisa deles, porque o efeito nasceu no SERVIDOR (cards órfãos
// remanejados numa troca de etapas, nota escrita por automação).
const RECONCILE_EVENTS = new Set([
  'pipeline.created',
  'pipeline.updated',
  'pipeline.deleted',
  'pipeline_card.activity_changed',
]);

// Bus events for pipeline real-time updates
export const PIPELINE_EVENTS = {
  CARD_CREATED: 'pipeline_card.created',
  CARD_UPDATED: 'pipeline_card.updated',
  CARD_DELETED: 'pipeline_card.deleted',
  CARD_STAGE_CHANGED: 'pipeline_card.stage_changed',
  CARD_WON: 'pipeline_card.won',
  CARD_LOST: 'pipeline_card.lost',
  CARD_OWNER_CHANGED: 'pipeline_card.owner_changed',
  CARD_REORDERED: 'pipeline_card.reordered',
  PIPELINE_CREATED: 'pipeline.created',
  PIPELINE_UPDATED: 'pipeline.updated',
  PIPELINE_DELETED: 'pipeline.deleted',
  ACTIVITY_CHANGED: 'pipeline_card.activity_changed',
  SEQUENCE_CHANGED: 'pipeline_sequence.changed',
  CONNECTION_CHANGED: 'pipeline_cable.connection_changed',
  // Evento PRÓPRIO do cable do analytics: os dois consumers são independentes,
  // e compartilhar o nome fazia o `connected` de um limpar a marca de queda do
  // outro durante a sobreposição de rotas (achado do cross-review).
  ANALYTICS_CONNECTION_CHANGED: 'pipeline_analytics_cable.connection_changed',
};

class PipelineCardCable {
  constructor() {
    this.consumer = null;
    this.subscription = null;
    // Subscription do PipelineSequenceChannel — mesmo consumer (fase-57 RT-P2-8).
    this.sequenceSubscription = null;
    this.store = null;
    // Hidratação pendente de cards criados remotamente (fase-57 RT-P1-4).
    this.hydrationTimer = null;
    this.pendingHydrationIds = null;
    this.currentPipelineId = null;
    this.currentAccountId = null;
    this.currentUserId = null;
    this.pubsubToken = null;
    // Id da ABA (mesmo que o APIHelper carimba no header das requests). É a
    // chave de supressão de eco — ver isOwnTabEcho.
    this.clientId = getClientSessionId();
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimer = null;
    // Handler de visibilidade da aba — guardado para poder ser removido no
    // disconnect (addEventListener exige a MESMA referência).
    this.visibilityHandler = null;
  }

  // Este cable pertence ao BOARD e só a ele. O dashboard analítico tem o
  // próprio (pipelineAnalyticsCable): compartilhar um singleton entre as duas
  // telas criava corridas de posse na troca de rota e deixava eventos de
  // OUTROS pipelines entrarem no store de cards do board (achados do
  // cross-review). Sem dono compartilhado, não há o que coordenar.
  init(store, pubsubToken) {
    if (this.consumer) {
      // Already initialized
      return this;
    }

    this.store = store;
    this.pubsubToken = pubsubToken;
    this.currentAccountId = store.getters.getCurrentAccountId;
    this.currentUserId = store.getters.getCurrentUserID;

    const { websocketURL = '' } = window.chatwootConfig || {};
    const wsUrl = websocketURL ? `${websocketURL}/cable` : undefined;

    this.consumer = createConsumer(wsUrl);
    this.startVisibilityWatch();

    return this;
  }

  // Aba oculta tem os timers estrangulados pelo navegador: o backoff de
  // reconexão pode nem rodar, e o board volta ao primeiro plano stale sem
  // ninguém perceber. Ao reaparecer, reassinamos na hora e ressincronizamos
  // por REST — o HTTP costuma voltar antes do WebSocket atrás de proxy.
  // (fase-57 RT-P0-3)
  startVisibilityWatch() {
    if (typeof document === 'undefined' || this.visibilityHandler) return;

    this.visibilityHandler = () => this.handleVisibilityChange();
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  stopVisibilityWatch() {
    if (typeof document === 'undefined' || !this.visibilityHandler) return;

    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.visibilityHandler = null;
  }

  handleVisibilityChange() {
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;
    // Conectado o tempo todo = não perdemos nada. O guard é pela SUBSCRIPTION,
    // não pelo pipeline: assinatura viva é o que define se havia algo a perder.
    if (this.isConnected || !this.subscription) return;

    this.reconnectAttempts = 0;
    this.maxAttemptsNotified = false;
    this.clearReconnectTimer();
    this.subscribe(this.currentPipelineId);

    // Passa pelo MESMO resyncTimer que o onConnected usa: se a reassinatura
    // vingar, o resync dele substitui este — nunca dois fetches seguidos.
    this.clearResyncTimer();
    this.resyncTimer = setTimeout(() => this.resyncAfterReconnect(), 1000);
  }

  subscribe(pipelineId) {
    if (!this.consumer || !this.store) {
      logger.warn('[PipelineCardCable] Not initialized. Call init() first.');
      return;
    }

    // Unsubscribe from previous pipeline
    if (this.subscription) {
      this.unsubscribe();
    }

    this.currentPipelineId = pipelineId;

    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'PipelineCardChannel',
        pubsub_token: this.pubsubToken,
        account_id: this.currentAccountId,
        user_id: this.currentUserId,
        pipeline_id: pipelineId,
      },
      {
        connected: () => this.onConnected(),
        disconnected: () => this.onDisconnected(),
        received: data => this.onReceived(data),
        rejected: () => this.onRejected(),
      }
    );

    this.subscribeSequences(pipelineId);

    // Assinando sem conexão viva (reconexão em andamento ou primeira conexão
    // que não sobe): garante o aviso de desconexão sustentada armado — o
    // guard interno evita duplicação e onConnected limpa.
    if (!this.isConnected) {
      this.startDisconnectNotifyTimer();
    }
  }

  // Segunda subscription no MESMO consumer (uma conexão WebSocket, dois
  // canais). O PipelineSequenceChannel e o PipelineSequences::BroadcastService
  // existiam desde a fase 25 sem NENHUM cliente assinando — as sequências
  // avançavam no servidor e a UI só descobria no F5 (fase-57 RT-P2-8).
  subscribeSequences(pipelineId) {
    this.unsubscribeSequences();
    if (!this.consumer || !this.store) return;

    this.sequenceSubscription = this.consumer.subscriptions.create(
      {
        channel: 'PipelineSequenceChannel',
        pubsub_token: this.pubsubToken,
        account_id: this.currentAccountId,
        user_id: this.currentUserId,
        pipeline_id: pipelineId,
      },
      {
        received: data => this.onSequenceReceived(data),
      }
    );
  }

  unsubscribeSequences() {
    if (!this.sequenceSubscription) return;

    this.sequenceSubscription.unsubscribe();
    this.sequenceSubscription = null;
  }

  onSequenceReceived(data) {
    if (!data?.event || !data.sequence || !this.store) return;
    if (this.isOwnTabEcho(data)) return;
    if (
      data.account_id != null &&
      Number(data.account_id) !== Number(this.currentAccountId)
    ) {
      return;
    }
    if (!this.isForCurrentPipeline(data.pipeline_id)) return;

    this.store.dispatch('pipelineCardSequences/applyRemoteSequenceEvent', data);
    emitter.emit(PIPELINE_EVENTS.SEQUENCE_CHANGED, data);
  }

  unsubscribe() {
    this.unsubscribeSequences();
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
      this.currentPipelineId = null;
    }
    this.clearReconnectTimer();
    this.clearResyncTimer();
    // NÃO limpar o disconnectNotifyTimer aqui: o loop de reconexão re-assina
    // via subscribe() → unsubscribe() ~1s após a queda e matava o aviso de
    // 35s (re-QA ciclo 11). O timer só é limpo ao CONECTAR (onConnected) —
    // e o callback dele já é no-op quando isConnected.
  }

  changePipeline(pipelineId) {
    if (!this.subscription) {
      this.subscribe(pipelineId);
      return;
    }

    if (this.currentPipelineId === pipelineId) {
      return;
    }

    // Use the channel's change_pipeline action
    this.subscription.perform('change_pipeline', { pipeline_id: pipelineId });
    this.currentPipelineId = pipelineId;
    // O PipelineSequenceChannel não tem ação `change_pipeline` — o stream do
    // agente é resolvido no subscribe. Reassina para o novo pipeline.
    this.subscribeSequences(pipelineId);
  }

  disconnect() {
    this.unsubscribe();
    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = null;
    }
    this.store = null;
    this.isConnected = false;
    // Teardown completo (saída da área do pipeline): aqui sim o aviso de
    // desconexão deixa de fazer sentido — e um toast agregado pendente não
    // pode disparar em outra tela.
    this.stopVisibilityWatch();
    this.clearDisconnectNotifyTimer();
    this.clearCreatedToastTimer();
    this.clearHydrationTimer();
  }

  // Connection event handlers
  onConnected() {
    // Reconexão (cobre tanto o nosso scheduleReconnect quanto o monitor
    // interno do ActionCable): eventos broadcast durante o gap foram
    // PERDIDOS — sem resync o board fica stale em silêncio.
    const wasReconnection = this.wasDisconnected === true;
    this.wasDisconnected = false;
    this.maxAttemptsNotified = false;
    this.isConnected = true;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    this.clearDisconnectNotifyTimer();

    emitter.emit(PIPELINE_EVENTS.CONNECTION_CHANGED, { connected: true });

    logger.log(
      '[PipelineCardCable] Connected to pipeline:',
      this.currentPipelineId
    );

    if (wasReconnection) {
      // Jitter 0-5s (achado Codex): após restart do servidor, todos os
      // clientes reconectam juntos — sem espalhar, o refetch simultâneo de
      // N boards vira thundering herd no Puma.
      const jitter = Math.floor(Math.random() * 5000);
      logger.log(
        `[PipelineCardCable] Reconnected — resyncing board state in ${jitter}ms`
      );
      this.clearResyncTimer();
      this.resyncTimer = setTimeout(() => this.resyncAfterReconnect(), jitter);
    }
  }

  clearResyncTimer() {
    if (this.resyncTimer) {
      clearTimeout(this.resyncTimer);
      this.resyncTimer = null;
    }
  }

  // Toast único quando a desconexão persiste por DISCONNECT_NOTIFY_AFTER_MS
  // — independe de quantos eventos 'disconnected' o ActionCable repassa.
  startDisconnectNotifyTimer() {
    if (this.disconnectNotifyTimer || this.maxAttemptsNotified) return;
    this.disconnectNotifyTimer = setTimeout(() => {
      this.disconnectNotifyTimer = null;
      if (!this.isConnected && !this.maxAttemptsNotified) {
        this.maxAttemptsNotified = true;
        this.showNotification(
          t('PIPELINE.NOTIFICATIONS.REALTIME_DISCONNECTED')
        );
      }
    }, DISCONNECT_NOTIFY_AFTER_MS);
  }

  clearDisconnectNotifyTimer() {
    if (this.disconnectNotifyTimer) {
      clearTimeout(this.disconnectNotifyTimer);
      this.disconnectNotifyTimer = null;
    }
  }

  // Evento de controle do backend: a lista de agents do pipeline mudou.
  // Re-assina — o channel re-avalia a visibilidade e REJEITA quem foi
  // removido (revogação efetiva do stream; sem isso o agente removido
  // seguia recebendo eventos até recarregar a página).
  handleMembershipChanged(data) {
    if (!this.isForCurrentPipeline(data.pipeline_id)) return;
    logger.log(
      '[PipelineCardCable] Pipeline membership changed — resubscribing'
    );
    this.subscribe(this.currentPipelineId);
  }

  // Refetch coerente com a view ativa: invalida o cache (senão o TTL
  // devolveria o estado stale) e refaz pelo MESMO modo do último load.
  async resyncBoard() {
    if (!this.store) return;
    try {
      const mode = this.store.state.pipelineCards?.lastFetchedMode;
      await this.store.dispatch('pipelineCards/resetCache');
      if (mode === 'global') {
        await this.store.dispatch('pipelineCards/fetchPipelineCardItems');
      } else {
        await this.store.dispatch('pipelineCards/fetchBoardByStages');
      }
    } catch (error) {
      logger.error('[PipelineCardCable] Board resync failed', error);
    }
  }

  // Mantido como nome próprio porque é o que os callers de reconexão pedem —
  // o comportamento é o mesmo refetch do resyncBoard.
  async resyncAfterReconnect() {
    await this.resyncBoard();
  }

  // O payload do broadcast é magro por design: 15 campos escalares. Engordá-lo
  // custa banda × N assinantes (foi por isso que o reorder teve que ser
  // enxugado na rodada 1). Então o card criado remotamente entra como STUB e é
  // hidratado logo em seguida com o card completo — contato, item_details,
  // labels, empresa, valor e campos customizados que o board realmente
  // renderiza. (fase-57 RT-P1-4)
  queueCardHydration(cardId) {
    if (!cardId || !this.store) return;

    if (!this.pendingHydrationIds) {
      this.pendingHydrationIds = new Set();
    }
    this.pendingHydrationIds.add(cardId);

    if (this.hydrationTimer) return;
    this.hydrationTimer = setTimeout(
      () => this.flushCardHydration(),
      HYDRATION_WINDOW_MS
    );
  }

  clearHydrationTimer() {
    if (this.hydrationTimer) {
      clearTimeout(this.hydrationTimer);
    }
    this.hydrationTimer = null;
    this.pendingHydrationIds = null;
  }

  async flushCardHydration() {
    const ids = [...(this.pendingHydrationIds || [])];
    const pipelineIdAtFlush = this.currentPipelineId;
    this.clearHydrationTimer();
    if (!ids.length || !this.store) return;

    // Lote grande (import CSV, automação em massa): N GETs viram tempestade no
    // Puma. Um refetch do board resolve tudo de uma vez e ainda realinha os
    // contadores das colunas com o servidor.
    if (ids.length > HYDRATION_MAX_INDIVIDUAL) {
      await this.resyncBoard();
      return;
    }

    // A chamada de API vive no STORE, não aqui: `dashboard/api/pipelineCards`
    // importa o próprio store, e trazer essa cadeia para dentro do helper de
    // cable criaria um ciclo de import (o store é montado antes do cable).
    // Todos os outros handlers já falam com o store — este segue a regra.
    await this.store.dispatch('pipelineCards/hydrateRemoteCards', {
      ids,
      pipelineId: pipelineIdAtFlush,
    });
  }

  onDisconnected() {
    this.isConnected = false;
    this.wasDisconnected = true;
    this.clearResyncTimer();
    // Aviso de desconexão por TEMPO, não por contagem de tentativas: o
    // ActionCable emite 'disconnected' UMA vez por queda (as ~26 falhas de
    // reconexão internas não repassam o evento), então o contador nunca
    // atinge o máximo em offline sustentado (achado QA ciclo 11).
    this.startDisconnectNotifyTimer();

    emitter.emit(PIPELINE_EVENTS.CONNECTION_CHANGED, { connected: false });

    logger.log('[PipelineCardCable] Disconnected');

    // Attempt to reconnect
    this.scheduleReconnect();
  }

  onRejected() {
    logger.warn(
      '[PipelineCardCable] Subscription rejected - check authentication'
    );
    this.isConnected = false;
  }

  // Reconnection logic
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.warn('[PipelineCardCable] Max reconnect attempts reached');
      // Avisa o usuário UMA vez — antes, o board ficava permanentemente
      // stale em silêncio após ~31s de backoff esgotado.
      if (!this.maxAttemptsNotified) {
        this.maxAttemptsNotified = true;
        this.showNotification(
          t('PIPELINE.NOTIFICATIONS.REALTIME_DISCONNECTED')
        );
      }
      return;
    }

    this.clearReconnectTimer();

    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts += 1;
      if (this.currentPipelineId) {
        this.subscribe(this.currentPipelineId);
      }
    }, delay);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // Message handler
  onReceived(data) {
    if (!data || !data.event) {
      return;
    }

    const { event } = data;

    // Ignora SÓ o eco da própria ABA — o update otimista dela já aplicou.
    // Antes a comparação era por `user_id`, o que descartava o evento em TODAS
    // as abas do autor: criar um card na tela da conversa deixava o board
    // aberto em outra aba parado até o F5 (fase-57 RT-P0-1). Eventos sem
    // `client_id` (job, automação, API externa) nunca são suprimidos.
    //
    // RECONCILE_EVENTS escapam da supressão porque NÃO espelham um update
    // otimista: mudar as etapas move cards órfãos no servidor
    // (move_orphan_cards!) e uma nota de automação muda a timeline. Suprimi-los
    // na aba autora a deixaria stale justamente onde ela não tinha o dado
    // (achado do cross-review).
    if (!RECONCILE_EVENTS.has(event) && this.isOwnTabEcho(data)) {
      return;
    }

    // Validate account (defense in depth — backend now includes account_id).
    if (
      data.account_id != null &&
      Number(data.account_id) !== Number(this.currentAccountId)
    ) {
      return;
    }

    // Route to appropriate handler
    const handlers = {
      'pipeline_card.created': () => this.handleCardCreated(data),
      'pipeline_card.updated': () => this.handleCardUpdated(data),
      'pipeline_card.deleted': () => this.handleCardDeleted(data),
      'pipeline_card.stage_changed': () => this.handleStageChanged(data),
      'pipeline_card.won': () => this.handleCardWon(data),
      'pipeline_card.lost': () => this.handleCardLost(data),
      'pipeline_card.owner_changed': () => this.handleOwnerChanged(data),
      'pipeline_card.reordered': () => this.handleCardsReordered(data),
      'pipeline_card.activity_changed': () => this.handleActivityChanged(data),
      'pipeline.created': () => this.handlePipelineCreated(data),
      'pipeline.updated': () => this.handlePipelineUpdated(data),
      'pipeline.deleted': () => this.handlePipelineDeleted(data),
      'pipeline.membership_changed': () => this.handleMembershipChanged(data),
    };

    const handler = handlers[event];
    if (handler) {
      handler();
    }
  }

  // O evento nasceu NESTA aba? Só então o eco é descartado: o update otimista
  // local já aplicou a mudança. Qualquer outra origem — outra aba do mesmo
  // usuário, outro usuário, job/automação — precisa ser aplicada.
  //
  // Exige client_id E user_id. O client_id viaja num header e é ecoado para os
  // outros clientes, então outro usuário autenticado poderia repetir o id de
  // uma aba alheia e fazê-la descartar eventos legítimos — deixando o board da
  // vítima parado (achado do cross-review). O user_id vem do servidor e fecha
  // isso: só suprimo o que foi atribuído a MIM, nesta aba.
  isOwnTabEcho(data) {
    const eventClientId = data?.triggered_by?.client_id;
    if (!eventClientId || eventClientId !== this.clientId) return false;

    return this.isSelfAuthored(data);
  }

  // O AUTOR do evento é o usuário logado (possivelmente em outra aba)? Serve
  // só para decidir TOAST: aplicar o dado é sempre correto, mas avisar "fulano
  // criou um card" quando o fulano é você mesmo é ruído.
  isSelfAuthored(data) {
    const userId = data?.triggered_by?.user_id;
    return userId != null && Number(userId) === Number(this.currentUserId);
  }

  // Whether an event scoped to `pipelineId` belongs to the pipeline the
  // user is currently viewing. With no pipeline selected, or when the
  // payload carries no pipeline info, accept it (never wrongly drop).
  isForCurrentPipeline(pipelineId) {
    if (!this.currentPipelineId) return true;
    if (pipelineId == null) return true;
    return Number(pipelineId) === Number(this.currentPipelineId);
  }

  // Card event handlers
  handleCardCreated(data) {
    const { card } = data;
    if (!card) return;

    // B54: ignore events for pipelines other than the currently viewed one
    if (!this.isForCurrentPipeline(card.pipeline_id)) return;

    this.store.commit('pipelineCards/ADD_PIPELINE_CARD_REMOTE', card);
    this.queueCardHydration(card.id);
    emitter.emit(PIPELINE_EVENTS.CARD_CREATED, data);

    // Toast só quando OUTRA pessoa criou. Desde que a supressão passou a ser
    // por aba (RT-P0-1), o autor recebe os próprios eventos nas demais abas —
    // aplicar o card ali é o objetivo, avisar "você criou um card" não é.
    if (this.isSelfAuthored(data)) return;

    this.queueCreatedToast(
      data.triggered_by?.user_name || t('PIPELINE.NOTIFICATIONS.ANOTHER_USER')
    );
  }

  // Agrupa toasts de criação numa janela curta: um import CSV de 300 cards
  // gerava 300 broadcasts → 300 toasts em quem estivesse com o board aberto
  // (re-auditoria rodada 2, achado #7). 1 card = toast normal; N de UM autor
  // = agregado com o nome; N de VÁRIOS autores = agregado genérico (last-wins
  // atribuiria tudo ao último — achado Codex).
  queueCreatedToast(userName) {
    this.createdToastCount = (this.createdToastCount || 0) + 1;
    this.createdToastUsers = this.createdToastUsers || new Set();
    this.createdToastUsers.add(userName);
    if (this.createdToastTimer) return;

    this.createdToastTimer = setTimeout(() => {
      const count = this.createdToastCount;
      const users = [...(this.createdToastUsers || [])];
      this.clearCreatedToastTimer();

      let message;
      if (count <= 1) {
        message = t('PIPELINE.NOTIFICATIONS.CARD_CREATED', { user: users[0] });
      } else if (users.length === 1) {
        message = t('PIPELINE.NOTIFICATIONS.CARDS_CREATED_BULK', {
          count,
          user: users[0],
        });
      } else {
        message = t('PIPELINE.NOTIFICATIONS.CARDS_CREATED_MULTI', { count });
      }
      this.showNotification(message);
    }, CREATED_TOAST_WINDOW_MS);
  }

  clearCreatedToastTimer() {
    if (this.createdToastTimer) {
      clearTimeout(this.createdToastTimer);
    }
    this.createdToastTimer = null;
    this.createdToastCount = 0;
    this.createdToastUsers = null;
  }

  handleCardUpdated(data) {
    const { card, changed_fields: changedFields } = data;
    if (!card) return;

    // B54: ignore events for pipelines other than the currently viewed one
    if (!this.isForCurrentPipeline(card.pipeline_id)) return;

    this.store.commit('pipelineCards/UPDATE_PIPELINE_CARD_REMOTE', {
      card,
      changedFields,
    });
    emitter.emit(PIPELINE_EVENTS.CARD_UPDATED, data);
  }

  handleCardDeleted(data) {
    const { card } = data;
    const cardId = card?.id;
    if (!cardId) return;

    // B54: ignore events for pipelines other than the currently viewed one
    if (!this.isForCurrentPipeline(card.pipeline_id)) return;

    // `stage` vem do payload para o badge da coluna fechar mesmo quando o card
    // apagado não estava carregado (board paginado).
    this.store.commit('pipelineCards/REMOVE_PIPELINE_CARD_REMOTE', {
      id: cardId,
      stage: card.stage ?? card.pipeline_stage ?? null,
    });
    emitter.emit(PIPELINE_EVENTS.CARD_DELETED, data);

    if (this.isSelfAuthored(data)) return;

    this.showNotification(
      t('PIPELINE.NOTIFICATIONS.CARD_DELETED', {
        user:
          data.triggered_by?.user_name ||
          t('PIPELINE.NOTIFICATIONS.ANOTHER_USER'),
      })
    );
  }

  handleStageChanged(data) {
    const { card, from_stage: fromStage, to_stage: toStage } = data;
    if (!card) return;

    // Ignore events for pipelines other than the currently viewed one.
    if (!this.isForCurrentPipeline(card.pipeline_id ?? data.pipeline_id)) {
      return;
    }

    this.store.commit('pipelineCards/UPDATE_CARD_STAGE_REMOTE', {
      cardId: card.id,
      stage: card.stage,
      position: card.position,
      fromStage,
      toStage,
      version: card.updated_at,
    });
    emitter.emit(PIPELINE_EVENTS.CARD_STAGE_CHANGED, data);
  }

  handleCardWon(data) {
    const { card } = data;
    if (!card) return;
    // Guard de pipeline (B54) — sem ele, vitórias em QUALQUER pipeline da
    // conta disparavam toast/commit enquanto você olha outro board.
    if (!this.isForCurrentPipeline(card.pipeline_id ?? data.pipeline_id)) {
      return;
    }

    this.store.commit('pipelineCards/UPDATE_PIPELINE_CARD_REMOTE', {
      card: { ...card, won_at: card.won_at },
    });
    emitter.emit(PIPELINE_EVENTS.CARD_WON, data);

    if (this.isSelfAuthored(data)) return;

    this.showNotification(
      t('PIPELINE.NOTIFICATIONS.CARD_WON', {
        user:
          data.triggered_by?.user_name ||
          t('PIPELINE.NOTIFICATIONS.ANOTHER_USER'),
      })
    );
  }

  handleCardLost(data) {
    const { card, lost_reason: lostReason } = data;
    if (!card) return;
    if (!this.isForCurrentPipeline(card.pipeline_id ?? data.pipeline_id)) {
      return;
    }

    this.store.commit('pipelineCards/UPDATE_PIPELINE_CARD_REMOTE', {
      card: { ...card, lost_at: card.lost_at, lost_reason: lostReason },
    });
    emitter.emit(PIPELINE_EVENTS.CARD_LOST, data);
  }

  handleOwnerChanged(data) {
    const { card, previous_owner_id: previousOwnerId } = data;
    if (!card) return;
    // Era o único handler sem o guard de pipeline (B54): troca de responsável
    // em QUALQUER pipeline da conta mexia no store de quem olhava outro board.
    // (fase-57 RT-P2-10)
    if (!this.isForCurrentPipeline(card.pipeline_id ?? data.pipeline_id)) {
      return;
    }

    this.store.commit('pipelineCards/UPDATE_PIPELINE_CARD_REMOTE', {
      card: { ...card, owner_id: card.owner_id },
      previousOwnerId,
    });
    emitter.emit(PIPELINE_EVENTS.CARD_OWNER_CHANGED, data);
  }

  handleCardsReordered(data) {
    const { cards, positions } = data;
    if (!cards || !positions) return;

    // Ignore events for pipelines other than the currently viewed one.
    if (!this.isForCurrentPipeline(data.pipeline_id)) return;

    this.store.commit('pipelineCards/REORDER_CARDS_REMOTE', {
      cards,
      positions,
      version: data.timestamp,
    });
    emitter.emit(PIPELINE_EVENTS.CARD_REORDERED, data);
  }

  // Atividade/nota mudou. Sem toast: automação escrevendo nota não é evento de
  // interesse do board. Quem consome é o card ABERTO, que refaz o fetch da
  // própria cópia (a timeline mora em item_details). (fase-57 RT-P2-9)
  handleActivityChanged(data) {
    const { card } = data;
    if (!card) return;
    if (!this.isForCurrentPipeline(card.pipeline_id ?? data.pipeline_id)) {
      return;
    }

    emitter.emit(PIPELINE_EVENTS.ACTIVITY_CHANGED, data);
  }

  // === Eventos do PIPELINE em si (fase-57 RT-P1-6) ===
  //
  // Estes handlers existiram, foram removidos na rodada 1 sob a premissa de que
  // "o backend não transmite eventos de pipeline" — e o
  // Pipelines::BroadcastService passou a transmitir sem que eles voltassem.
  // Resultado: mudar etapas, cores ou nome do pipeline não chegava em ninguém.
  // As mutations `pipelines/*_PIPELINE_REMOTE` sempre estiveram no store (são
  // as mesmas que as actions locais usam) — aqui só religamos o roteamento.

  handlePipelineCreated(data) {
    const pipeline = data?.pipeline;
    if (!pipeline?.id) return;

    this.store.commit('pipelines/ADD_PIPELINE_REMOTE', pipeline);
    emitter.emit(PIPELINE_EVENTS.PIPELINE_CREATED, data);
  }

  handlePipelineUpdated(data) {
    const pipeline = data?.pipeline;
    if (!pipeline?.id) return;

    this.store.commit('pipelines/UPDATE_PIPELINE_REMOTE', pipeline);
    emitter.emit(PIPELINE_EVENTS.PIPELINE_UPDATED, data);

    // Mudança de ETAPAS reorganiza o board: o backend move os cards órfãos
    // (move_orphan_cards!) na mesma request, então o flat local ficaria
    // apontando para etapas que não existem mais. Só refaz o fetch quando é o
    // pipeline que está na tela — trocar as etapas de outro pipeline não pode
    // custar um refetch a quem nem está olhando para ele.
    const changedFields = data?.changed_fields || [];
    if (
      changedFields.includes('stages') &&
      this.isForCurrentPipeline(pipeline.id)
    ) {
      this.resyncBoard();
    }
  }

  handlePipelineDeleted(data) {
    const pipelineId = data?.pipeline?.id;
    if (!pipelineId) return;

    this.store.commit('pipelines/REMOVE_PIPELINE_REMOTE', pipelineId);
    emitter.emit(PIPELINE_EVENTS.PIPELINE_DELETED, data);
  }

  // Utility methods
  // eslint-disable-next-line class-methods-use-this
  showNotification(message) {
    emitter.emit('newToastMessage', { message });
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      currentPipelineId: this.currentPipelineId,
      reconnectAttempts: this.reconnectAttempts,
    };
  }

  isInitialized() {
    return Boolean(this.consumer && this.store);
  }
}

// Singleton instance
const pipelineCardCable = new PipelineCardCable();

export default pipelineCardCable;
